/**
 * Pure rules for the "a new version is available" prompt. No React, no DOM
 * beyond guarded storage access, so every rule here is unit-tested.
 *
 * Product rules live in `design_handoff_version_update/README.md`: three
 * urgency levels set per deploy, a grace period for `normal`, snooze by time
 * (never by version), and a `required` floor judged by deploy order.
 */

export type Urgency = 'none' | 'normal' | 'soon' | 'required';

/** `version.json`, as written by `scripts/generate-version.js`. */
export interface RemoteVersion {
  /** Current key. */
  build?: string;
  /** Legacy key still read by clients deployed before `build` existed. */
  buildHash?: string;
  buildTimestamp?: number;
  urgency?: string;
  minBuild?: string;
  /** Deploy order of `minBuild`. Hashes do not sort, so the floor is a time. */
  minBuildTimestamp?: number;
  reason?: string;
}

export interface RunningBuild {
  hash: string;
  timestamp: number;
}

/** A `normal` update only shows once the build has been out this long. */
export const GRACE_MS = 15 * 60 * 1000;
export const POLL_MS = 30 * 60 * 1000;
export const SNOOZE_LATER_MS = 8 * 60 * 60 * 1000;
export const SNOOZE_SOON_MS = 60 * 60 * 1000;
export const NORMAL_TOAST_MS = 12 * 1000;
export const REQUIRED_COUNTDOWN_S = 30;

export const SNOOZE_KEY = 'motzu.update.snoozedUntil';
export const TOAST_SHOWN_KEY = 'motzu.update.toastShown';
export const RESTORE_KEY = 'motzu.restore';

export const remoteBuildOf = (remote: RemoteVersion | null): string => {
  const value = remote?.build ?? remote?.buildHash;
  return typeof value === 'string' ? value : '';
};

const parseUrgency = (value: unknown): Exclude<Urgency, 'none'> =>
  value === 'soon' || value === 'required' ? value : 'normal';

/**
 * What the deploy asks for, before the grace period.
 *
 * `required` only binds a client older than the floor. A client one deploy
 * behind the floor is not broken, so it gets `soon` (the deploy is important
 * enough to be marked required, but this tab still talks to the server).
 */
export function deriveUrgency(remote: RemoteVersion | null, running: RunningBuild): Urgency {
  const remoteBuild = remoteBuildOf(remote);
  if (!remote || !remoteBuild || !running.hash || remoteBuild === running.hash) return 'none';
  const requested = parseUrgency(remote.urgency);
  if (requested !== 'required') return requested;
  const floor = remote.minBuildTimestamp;
  return typeof floor === 'number' && running.timestamp < floor ? 'required' : 'soon';
}

/** When the new build became available: its build time, else first sight. */
export function availableSinceOf(remote: RemoteVersion | null, firstSeenAt: number): number {
  const built = remote?.buildTimestamp;
  return typeof built === 'number' && built > 0 ? Math.min(built, firstSeenAt) : firstSeenAt;
}

/** `normal` waits out the grace period; `soon` and `required` never do. */
export function applyGrace(urgency: Urgency, availableSince: number, now: number): Urgency {
  if (urgency === 'normal' && now - availableSince < GRACE_MS) return 'none';
  return urgency;
}

export const isSnoozed = (snoozedUntil: number | undefined, now: number): boolean =>
  typeof snoozedUntil === 'number' && snoozedUntil > now;

export const snoozeDuration = (urgency: Urgency): number =>
  urgency === 'soon' ? SNOOZE_SOON_MS : SNOOZE_LATER_MS;

const safe = <T>(fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

export const readSnooze = (): number | undefined =>
  safe(() => {
    const raw = window.localStorage.getItem(SNOOZE_KEY);
    const at = raw ? Date.parse(raw) : NaN;
    return Number.isFinite(at) ? at : undefined;
  }, undefined);

export const writeSnooze = (until: number): void =>
  safe(() => window.localStorage.setItem(SNOOZE_KEY, new Date(until).toISOString()), undefined);

export const toastAlreadyShown = (): boolean =>
  safe(() => window.sessionStorage.getItem(TOAST_SHOWN_KEY) === '1', false);

export const markToastShown = (): void =>
  safe(() => window.sessionStorage.setItem(TOAST_SHOWN_KEY, '1'), undefined);

export interface RestoreState {
  path: string;
  tab: string | null;
}

/** Remember where the person was; called right before any programmatic reload. */
export function saveRestoreState(): void {
  safe(() => {
    const { pathname, search } = window.location;
    const tab = new URLSearchParams(search).get('tab');
    const state: RestoreState = { path: `${pathname}${search}`, tab };
    window.sessionStorage.setItem(RESTORE_KEY, JSON.stringify(state));
  }, undefined);
}

/** Read and clear the saved place. Returns `null` when there is none. */
export function takeRestoreState(): RestoreState | null {
  return safe(() => {
    const raw = window.sessionStorage.getItem(RESTORE_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(RESTORE_KEY);
    const parsed = JSON.parse(raw) as Partial<RestoreState>;
    // Only same-app paths: this value feeds navigate().
    if (typeof parsed.path !== 'string' || !parsed.path.startsWith('/') || parsed.path.startsWith('//')) {
      return null;
    }
    return { path: parsed.path, tab: typeof parsed.tab === 'string' ? parsed.tab : null };
  }, null);
}

export function reloadApp(): void {
  saveRestoreState();
  window.location.reload();
}
